/**
 * Proximity Enable Modal
 * ======================
 * First-time activation flow with privacy explanation.
 * Shows before requesting device location permission.
 */

import React, { useState, useEffect } from 'react';
import { X, Radar, Shield, Eye, EyeOff, MapPin } from 'lucide-react';
import { useProximity } from '../context/ProximityContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProximityEnableModal = ({ isOpen, onClose, onSuccess }) => {
  const { enableProximity } = useProximity();
  const [privacyNotice, setPrivacyNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Privacy explanation, 2: Permission request

  useEffect(() => {
    if (isOpen) {
      fetchPrivacyNotice();
      setStep(1);
      setError(null);
    }
  }, [isOpen]);

  const fetchPrivacyNotice = async () => {
    try {
      const res = await fetch(`${API}/proximity/privacy-notice`);
      if (res.ok) {
        const data = await res.json();
        setPrivacyNotice(data);
      }
    } catch (error) {
      console.error('Failed to fetch privacy notice:', error);
    }
  };

  const handleContinue = () => {
    setStep(2);
  };

  const handleEnable = async () => {
    setLoading(true);
    setError(null);

    const result = await enableProximity();
    
    setLoading(false);

    if (result.success) {
      onSuccess?.();
      onClose();
    } else {
      setError(result.error || 'Failed to enable proximity discovery');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(10px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div 
        style={{
          background: 'linear-gradient(145deg, #1A0F0A 0%, #2C1B13 100%)',
          border: '1px solid rgba(165, 124, 51, 0.4)',
          borderRadius: '20px',
          maxWidth: '400px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        data-testid="proximity-enable-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(138, 115, 85, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#8A7355'
          }}
        >
          <X size={18} />
        </button>

        {/* Content */}
        <div style={{ padding: '32px 24px' }}>
          {step === 1 ? (
            <>
              {/* Privacy Explanation Step */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(56, 142, 60, 0.1) 100%)',
                  border: '2px solid rgba(76, 175, 80, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <Radar size={36} color="#4CAF50" />
                </div>
                
                <h2 style={{
                  fontSize: '22px',
                  fontFamily: 'Cinzel, serif',
                  background: 'linear-gradient(135deg, #D7B56D 0%, #E6D7C3 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}>
                  {privacyNotice?.title || 'Turn on Proximity Discovery?'}
                </h2>
                
                <p style={{
                  color: '#A57C33',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  {privacyNotice?.description || 'Discover members within 60 feet who also have this enabled.'}
                </p>
              </div>

              {/* Privacy Points */}
              <div style={{ marginBottom: '24px' }}>
                {(privacyNotice?.privacy_points || [
                  'Other members will never see your exact location.',
                  'While Proximity Discovery is active, members within the discovery radius may see your profile.',
                  'You can turn off Proximity Discovery at any time.',
                  'Your location is only used while the app is active.'
                ]).map((point, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(76, 175, 80, 0.05)',
                      borderRadius: '10px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'rgba(76, 175, 80, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {index === 0 ? <EyeOff size={14} color="#4CAF50" /> :
                       index === 1 ? <Eye size={14} color="#4CAF50" /> :
                       index === 2 ? <Shield size={14} color="#4CAF50" /> :
                       <MapPin size={14} color="#4CAF50" />}
                    </div>
                    <p style={{
                      color: '#E6D7C3',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      margin: 0
                    }}>
                      {point}
                    </p>
                  </div>
                ))}
              </div>

              {/* Legal Notice */}
              <p style={{
                color: '#8A7355',
                fontSize: '11px',
                textAlign: 'center',
                marginBottom: '24px',
                fontStyle: 'italic'
              }}>
                {privacyNotice?.legal_notice || '[FINAL PRIVACY LANGUAGE SUBJECT TO LEGAL REVIEW]'}
              </p>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'transparent',
                    border: '1px solid rgba(165, 124, 51, 0.4)',
                    borderRadius: '12px',
                    color: '#A57C33',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  data-testid="proximity-not-now-btn"
                >
                  Not Now
                </button>
                <button
                  onClick={handleContinue}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#FFF',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)'
                  }}
                  data-testid="proximity-continue-btn"
                >
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Permission Request Step */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                  border: '2px solid rgba(215, 181, 109, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <MapPin size={36} color="#D7B56D" />
                </div>
                
                <h2 style={{
                  fontSize: '22px',
                  fontFamily: 'Cinzel, serif',
                  background: 'linear-gradient(135deg, #D7B56D 0%, #E6D7C3 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}>
                  Allow Location Access
                </h2>
                
                <p style={{
                  color: '#A57C33',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  marginBottom: '16px'
                }}>
                  NearU needs your location to find members nearby.
                </p>

                <div style={{
                  background: 'rgba(215, 181, 109, 0.1)',
                  border: '1px solid rgba(215, 181, 109, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{
                    color: '#E6D7C3',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    <strong style={{ color: '#D7B56D' }}>Your privacy is protected:</strong>
                    <br />
                    Your exact location is never shared with other members. 
                    They only see that you're "Nearby" or "Within 60 ft".
                  </p>
                </div>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '10px',
                  padding: '12px',
                  marginBottom: '20px'
                }}>
                  <p style={{
                    color: '#F44336',
                    fontSize: '13px',
                    margin: 0,
                    textAlign: 'center'
                  }}>
                    {error}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setStep(1)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'transparent',
                    border: '1px solid rgba(165, 124, 51, 0.4)',
                    borderRadius: '12px',
                    color: '#A57C33',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleEnable}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: loading 
                      ? 'rgba(215, 181, 109, 0.5)' 
                      : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#2C1B13',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: loading ? 'wait' : 'pointer',
                    boxShadow: '0 4px 16px rgba(215, 181, 109, 0.3)'
                  }}
                  data-testid="proximity-enable-btn"
                >
                  {loading ? 'Enabling...' : 'Enable'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProximityEnableModal;
